import {
  AppAnalytics,
  AppAnalyticsDoc,
  PerformanceStats,
} from "@/types/v2/AppAnalytics";
import shortUUID from "short-uuid";
import { localStorageManager } from "@/services/localStorage";
import { config } from "@/config";
import { auth } from "@/services/auth";
import { format } from "date-fns";
import { generic } from "@/repositories/generic";
import { LOCAL_STORAGE_BASE_KEYS } from "@/types/v2";

export const AppAnalyticsRepository = {
  currentId: () => format(new Date(), "yyyyMMdd"),
  path: (shopId: string) => `shops/${shopId}/appAnalytics`,
  getAnalytics: (shopId: string) => {
    const analyticId = AppAnalyticsRepository.currentId();
    return generic.get<AppAnalyticsDoc>(
      analyticId,
      AppAnalyticsRepository.path(shopId)
    );
  },
  createAnalytics: (shopId: string, analytics: AppAnalytics) => {
    const analyticId = AppAnalyticsRepository.currentId();
    return generic.create<AppAnalyticsDoc>(
      { id: analyticId, appAnalytics: [analytics] },
      `${AppAnalyticsRepository.path(shopId)}/${AppAnalyticsRepository.currentId()}`
    );
  },
  updateAnalytics: (shopId: string, analytics: AppAnalytics[]) => {
    return generic.update<AppAnalyticsDoc>(
      { appAnalytics: analytics },
      `${AppAnalyticsRepository.path(shopId)}/${AppAnalyticsRepository.currentId()}`
    );
  },
  pushAnalytics: async (shopId: string, analytics: AppAnalytics) => {
    const analyticData = await AppAnalyticsRepository.getAnalytics(shopId);
    if (!analyticData) {
      return AppAnalyticsRepository.createAnalytics(shopId, analytics);
    }
    analyticData.appAnalytics.push(analytics);
    return AppAnalyticsRepository.updateAnalytics(
      shopId,
      analyticData.appAnalytics
    );
  },
  generateBrowserId: () => {
    const browserId = shortUUID.generate();
    localStorageManager.setItem(LOCAL_STORAGE_BASE_KEYS.BROWSER_ID, browserId);
    return browserId;
  },
  getBrowserId: () => {
    const browserId = localStorageManager.getItem(
      LOCAL_STORAGE_BASE_KEYS.BROWSER_ID
    );
    if (!browserId) {
      return AppAnalyticsRepository.generateBrowserId();
    }
    return browserId;
  },
  generateCurrentAnalytics: (
    numberSubscribedDocuments: number,
    subscribedDocuments: { [x: string]: number },
    performanceStats: PerformanceStats
  ): AppAnalytics => {
    return {
      browserId: AppAnalyticsRepository.getBrowserId(),
      createdAt: new Date(),
      appVersion: `${config.version}-${config.build}-${config.commitHash}`,
      numSubscribedDocuments: numberSubscribedDocuments,
      subscribedDocuments,
      performanceStats,
      uid: auth.currentUser?.uid ?? "",
    };
  },
  logAnalytics: async (
    shopId: string,
    numberSubscribedDocuments: number,
    subscribedDocuments: { [x: string]: number },
    performanceStats: PerformanceStats
  ) => {
    const analytics = AppAnalyticsRepository.generateCurrentAnalytics(
      numberSubscribedDocuments,
      subscribedDocuments,
      performanceStats
    );
    await AppAnalyticsRepository.pushAnalytics(shopId, analytics);
    localStorageManager.setItem(
      LOCAL_STORAGE_BASE_KEYS.ANALYTICS_TIMESTAMP,
      new Date().getTime()
    );
    return;
  },
};
